import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Radio, Award, User, Trophy,
  Shield, LogOut, Search, Moon, Sun, Bell, MessageCircle, Send,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useSettings } from "@/context/SettingsContext";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import api from "@/lib/api";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/app/courses", icon: BookOpen, label: "Courses" },
  { to: "/app/live", icon: Radio, label: "Live Classes" },
  { to: "/app/certificates", icon: Award, label: "Certificates" },
  { to: "/app/leaderboard", icon: Trophy, label: "Leaderboard" },
  { to: "/app/profile", icon: User, label: "Profile" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  async function search(e) {
    e?.preventDefault();
    if (!q.trim()) return setResults(null);
    const { data } = await api.get(`/search`, { params: { q } });
    setResults(data);
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 border-r border-border/60 glass z-30">
          <div className="p-5">
            <Logo size={40} />
          </div>
          <nav className="flex-1 px-3 space-y-1">
            {NAV.map(({ to, icon: Icon, label, end }) => (
              <NavLink
                key={to} to={to} end={end}
                data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_rgba(30,58,138,0.55)]"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/70"
                  }`
                }
              >
                <Icon className="h-4 w-4" /> {label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/app/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium mt-4 border border-gold/40 ${
                    isActive ? "bg-gold text-[#1E3A8A]" : "text-gold hover:bg-gold/10"
                  }`
                }
              >
                <Shield className="h-4 w-4" /> Admin
              </NavLink>
            )}
          </nav>
          <div className="p-3 space-y-2 border-t border-border/60">
            <a href={settings.whatsapp_community} target="_blank" rel="noreferrer" data-testid="whatsapp-community"
               className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 transition">
              <MessageCircle className="h-4 w-4" /> WhatsApp Community
            </a>
            <a href={settings.telegram_link} target="_blank" rel="noreferrer" data-testid="telegram-community"
               className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm bg-sky-500/10 text-sky-600 dark:text-sky-400 hover:bg-sky-500/20 transition">
              <Send className="h-4 w-4" /> Telegram
            </a>
            <Button
              variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-btn"
              className="w-full justify-start text-muted-foreground">
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </Button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 lg:ml-64">
          {/* Header */}
          <header className="sticky top-0 z-20 glass border-b border-border/60">
            <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
              <div className="lg:hidden"><Logo variant="mark" size={30} /></div>
              <form onSubmit={search} className="flex-1 max-w-xl relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="global-search-input"
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search courses, lessons, PDFs..."
                  className="pl-9 rounded-full bg-secondary/50 border-transparent focus-visible:ring-primary/30"
                />
                {results && (
                  <div className="absolute mt-2 w-full rounded-2xl bg-card border border-border shadow-xl p-3 max-h-96 overflow-auto z-40">
                    <button className="absolute top-2 right-3 text-xs text-muted-foreground" onClick={() => { setResults(null); setQ(""); }}>Clear</button>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Courses</p>
                    {results.courses.length === 0 && <p className="text-sm text-muted-foreground mb-3">No courses found.</p>}
                    {results.courses.map((c) => (
                      <button key={c.id} onClick={() => { nav(`/app/courses/${c.slug}`); setResults(null); setQ(""); }}
                              data-testid={`search-course-${c.slug}`}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary transition flex items-center gap-3">
                        <img src={c.thumbnail} alt="" className="h-8 w-12 rounded object-cover" />
                        <span className="text-sm">{c.title}</span>
                      </button>
                    ))}
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mt-3 mb-2">Lessons</p>
                    {results.lessons.length === 0 && <p className="text-sm text-muted-foreground">No lessons found.</p>}
                    {results.lessons.map((l) => (
                      <button key={l.id} onClick={() => { nav(`/app/courses/${l.course_slug}`); setResults(null); setQ(""); }}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary transition text-sm">
                        {l.title} <span className="text-muted-foreground text-xs">· {l.course_title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </form>
              <button onClick={toggle} data-testid="theme-toggle"
                      className="h-9 w-9 rounded-full flex items-center justify-center bg-secondary/60 hover:bg-secondary transition">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <button data-testid="notifications-btn" className="h-9 w-9 rounded-full flex items-center justify-center bg-secondary/60 relative">
                <Bell className="h-4 w-4" />
                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-gold" />
              </button>
              <div className="hidden sm:flex items-center gap-2 pl-2">
                <div className="h-9 w-9 rounded-full royal-gradient flex items-center justify-center text-white text-xs font-bold">
                  {user?.name?.[0]?.toUpperCase() || "S"}
                </div>
                <div className="leading-tight hidden md:block">
                  <p className="text-xs font-semibold">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{user?.role}</p>
                </div>
              </div>
            </div>
          </header>

          <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 relative royal-radial min-h-[calc(100vh-64px)]">
            {children}
          </div>

          <footer className="border-t border-border/60 mt-10 py-6 px-6 text-xs text-muted-foreground text-center">
            <p className="font-semibold text-foreground">{settings.platform_name}</p>
            <p className="mt-1">{settings.footer_text}</p>
          </footer>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass-strong border-t border-border/60 grid grid-cols-5 px-2 py-1.5">
        {NAV.slice(0, 5).map(({ to, icon: Icon, label, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) =>
            `flex flex-col items-center gap-0.5 py-1.5 text-[10px] ${isActive ? "text-primary dark:text-gold" : "text-muted-foreground"}`
          }>
            <Icon className="h-4 w-4" /> {label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
